import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    Fade,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Popper,
    Snackbar,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ELinks } from '@sharedInterfaces/globalEnums';
import type { ILevel } from '@sharedInterfaces/ICompanySettings';
import type { ISkillReferenceDTO } from '@sharedInterfaces/ISkill';
import getEligibleSuperPowerSkills from '@src/APIs/graphQl/Employee/getEligibleSuperPowerSkills';
import markSkillAsSuperPower from '@src/APIs/graphQl/Employee/markSkillAsSuperPower';
import reorderSuperPowers from '@src/APIs/graphQl/Employee/reorderSuperPowers';
import ItemBadge from '@src/App/Components/Items/ItemBadge/ItemBadge';
import { EIconCategory, getAppIcon } from '@src/helper/iconRegistry';
import type Employee from '@src/Objects/Employee';
import { setEmployeeSkills } from '@store/reducer/employeeReducer';
import type { AppState } from '@store/store';
import { Bot, Crown, Eye, HandMetal, Info, Plus, Search, Sparkles, Star, Target, TrendingUp, X } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import StatsCards, { type StatsTile } from './components/StatsCards';

interface SuperPowersTabProps {
    employee: Employee;
    onTabChange?: (tabIndex: number) => void;
}

function SuperPowersTab({ employee, onTabChange }: SuperPowersTabProps) {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    // const lang = useSelector((state: AppState) => state.employee.language);
    const levelDefinitions = useSelector((state: AppState) => state.levelDefinition);
    const reduxEmployee = useSelector((state: AppState) => state.employee);
    const [editMode, setEditMode] = useState(false);
    const [infoAnchor, setInfoAnchor] = useState<null | HTMLElement>(null);
    const [addMenuAnchor, setAddMenuAnchor] = useState<null | HTMLElement>(null);
    const [showLimitError, setShowLimitError] = useState(false);
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [searchValue, setSearchValue] = useState<ISkillReferenceDTO | null>(null);
    const [eligibleSkills, setEligibleSkills] = useState<ISkillReferenceDTO[]>([]);

    const skills = useMemo(() => reduxEmployee.skills || [], [reduxEmployee.skills]);
    const [localOrder, setLocalOrder] = useState<number[]>([]);

    const currentSuperPowers = useMemo(() => {
        const superPowers = skills.filter((skill) => skill.isSuperPower === true);
        const order = localOrder.length > 0 ? localOrder : reduxEmployee.superPowerOrder || [];
        console.log('Frontend - localOrder:', localOrder);
        console.log('Frontend - reduxEmployee.superPowerOrder:', reduxEmployee.superPowerOrder);
        console.log('Frontend - using order:', order);
        if (order.length === 0) return superPowers;

        const orderMap = new Map(order.map((id, index) => [id, index]));
        return superPowers.sort((a, b) => {
            const orderA = orderMap.get(a.id) ?? Number.MAX_SAFE_INTEGER;
            const orderB = orderMap.get(b.id) ?? Number.MAX_SAFE_INTEGER;
            return orderA - orderB;
        });
    }, [skills, localOrder, reduxEmployee.superPowerOrder]);
    const maxLevel = useMemo(
        () => (levelDefinitions.length > 0 ? Math.max(...levelDefinitions.map((l: ILevel) => l.level)) : 4),
        [levelDefinitions],
    );
    const highestLevelSkills = useMemo(() => skills.filter((skill) => skill.level === maxLevel), [skills, maxLevel]);
    const avgSuperPowerLevel = useMemo(
        () =>
            currentSuperPowers.length > 0
                ? currentSuperPowers.reduce((sum, skill) => sum + skill.level, 0) / currentSuperPowers.length
                : 0,
        [currentSuperPowers],
    );

    const tiles: StatsTile[] = useMemo(
        () => [
            {
                title: 'SuperPowers',
                value: currentSuperPowers.length.toString(),
                icon: <Sparkles size={32} strokeWidth={1.5} />,
                color: '#ffb74d',
                bgColor: alpha('#ffb74d', 0.1),
                borderColor: alpha('#ffb74d', 0.3),
            },
            {
                title: `Level ${maxLevel} Skills`,
                value: highestLevelSkills.length.toString(),
                icon: <Star size={32} strokeWidth={1.5} />,
                color: theme.palette.primary.main,
                bgColor: alpha(theme.palette.primary.main, 0.1),
                borderColor: alpha(theme.palette.primary.main, 0.3),
            },
            {
                title: 'Av. SP Level',
                value: avgSuperPowerLevel.toFixed(1),
                icon: <TrendingUp size={32} strokeWidth={1.5} />,
                color: theme.palette.secondary.main,
                bgColor: alpha(theme.palette.secondary.main, 0.1),
                borderColor: alpha(theme.palette.secondary.main, 0.3),
            },
        ],
        [currentSuperPowers.length, maxLevel, highestLevelSkills.length, avgSuperPowerLevel, theme.palette],
    );

    const availableSkills = useMemo(
        () =>
            eligibleSkills
                .filter((skill) => !skill.isSuperPower && !currentSuperPowers.find((sp) => sp.id === skill.id))
                .sort((a, b) => b.level - a.level),
        [eligibleSkills, currentSuperPowers],
    );

    useEffect(() => {
        getEligibleSuperPowerSkills()
            .then(setEligibleSkills)
            .catch((error) => console.error('Failed to load eligible skills:', error));
    }, []);

    const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    }, []);

    const dispatch = useDispatch();

    const handleDrop = useCallback(
        async (e: React.DragEvent, dropIndex: number) => {
            e.preventDefault();
            if (draggedIndex === null) return;

            const newSuperPowers = [...currentSuperPowers];
            const draggedItem = newSuperPowers[draggedIndex];
            newSuperPowers.splice(draggedIndex, 1);
            newSuperPowers.splice(dropIndex, 0, draggedItem);

            setDraggedIndex(null);
            setLocalOrder(newSuperPowers.map((sp) => sp.id));

            try {
                await reorderSuperPowers(newSuperPowers.map((sp) => sp.id));
            } catch (error) {
                console.error('Failed to reorder superpowers:', error);
                setLocalOrder([]);
            }
        },
        [draggedIndex, currentSuperPowers],
    );

    const handleDelete = useCallback(
        async (skillId: number) => {
            const updatedSkills = skills.map((skill) =>
                skill.id === skillId ? { ...skill, isSuperPower: undefined } : skill,
            );
            dispatch(setEmployeeSkills(updatedSkills));

            try {
                await markSkillAsSuperPower(skillId, false);
            } catch (error) {
                console.error('Failed to remove superpower:', error);
                dispatch(setEmployeeSkills(skills));
            }
        },
        [skills, dispatch],
    );

    const handleAddSkill = useCallback(
        async (skill: ISkillReferenceDTO) => {
            if (currentSuperPowers.find((sp) => sp.id === skill.id)) return;
            if (currentSuperPowers.length >= 6) {
                setShowLimitError(true);
                return;
            }

            const updatedSkills = skills.map((s) => (s.id === skill.id ? { ...s, isSuperPower: true as true } : s));
            dispatch(setEmployeeSkills(updatedSkills));
            setAddMenuAnchor(null);

            try {
                await markSkillAsSuperPower(skill.id, true);
            } catch (error) {
                console.error('Failed to add superpower:', error);
                dispatch(setEmployeeSkills(skills));
            }
        },
        [currentSuperPowers, skills, dispatch],
    );

    // Click away listener for info popper
    useEffect(() => {
        const handleClickAway = (event: MouseEvent) => {
            if (infoAnchor && !infoAnchor.contains(event.target as Node)) {
                setInfoAnchor(null);
            }
        };

        if (infoAnchor) {
            document.addEventListener('click', handleClickAway);
            return () => document.removeEventListener('click', handleClickAway);
        }
    }, [infoAnchor]);

    const getLevelIcon = useCallback((level: number) => {
        if (level === 4) return <Crown size={16} />;
        if (level === 3) return <Star size={16} />;
        return <TrendingUp size={16} />;
    }, []);

    const getLevelText = useCallback(
        (level: number) => {
            const levelDef = levelDefinitions.find((l: ILevel) => l.level === level);
            return levelDef?.title || `Level ${level}`;
        },
        [levelDefinitions],
    );

    const getLevelColor = useCallback(
        (level: number, isMain = false) => {
            if (isMain) return theme.palette.accent?.main || theme.palette.secondary.main;
            return theme.palette.levels?.[level as 1 | 2 | 3 | 4] || theme.palette.primary.main;
        },
        [theme.palette],
    );

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                        SuperPowers
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={(e) => setInfoAnchor(infoAnchor ? null : e.currentTarget)}
                        sx={{ color: theme.palette.text.secondary }}
                    >
                        <Info size={16} />
                    </IconButton>
                    <Popper open={Boolean(infoAnchor)} anchorEl={infoAnchor} placement="bottom-start" transition>
                        {({ TransitionProps }) => (
                            <Fade {...TransitionProps}>
                                <Paper
                                    sx={{
                                        p: 2,
                                        maxWidth: 300,
                                        bgcolor: theme.palette.background.paper,
                                        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                                        boxShadow: theme.shadows[8],
                                    }}
                                >
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                        Was sind SuperPowers?
                                    </Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                        SuperPowers sind deine stärksten Skills (höchste oder zweithöchste Stufe). Du
                                        kannst bis zu 6 auswählen und einen als Focus Power definieren.
                                    </Typography>
                                </Paper>
                            </Fade>
                        )}
                    </Popper>
                </Box>

                {editMode ? (
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setEditMode(false)}
                        sx={{
                            textTransform: 'none',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                        }}
                    >
                        Fertig
                    </Button>
                ) : (
                    <IconButton
                        size="small"
                        onClick={() => setEditMode(true)}
                        aria-label="SuperPowers bearbeiten"
                        title="SuperPowers bearbeiten"
                        sx={{
                            color: theme.palette.primary.main,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                            '&:hover': {
                                borderColor: theme.palette.primary.main,
                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                            },
                        }}
                    >
                        {getAppIcon(EIconCategory.EDIT)}
                    </IconButton>
                )}
            </Box>

            <Typography variant="body2" sx={{ opacity: 0.8, mb: 3 }}>
                Deine stärksten Skills und Expertisen
            </Typography>

            <StatsCards tiles={tiles} />

            {currentSuperPowers.length > 0 ? (
                editMode ? (
                    // Edit Mode - Table View
                    <Box>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '2fr 1fr 1fr 80px',
                                gap: 2,
                                p: 2,
                                bgcolor: alpha(theme.palette.primary.main, 0.05),
                                borderRadius: '8px 8px 0 0',
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                borderBottom: 'none',
                            }}
                        >
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                Skill
                            </Typography>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, textAlign: 'center' }}>
                                Level
                            </Typography>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, textAlign: 'center' }}>
                                Status
                            </Typography>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, textAlign: 'center' }}>
                                Aktionen
                            </Typography>
                        </Box>
                        {currentSuperPowers.map((skill, index) => (
                            <Box
                                key={skill.id}
                                draggable
                                onDragStart={(e) => handleDragStart(e, index)}
                                onDragOver={handleDragOver}
                                onDrop={(e) => handleDrop(e, index)}
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '2fr 1fr 1fr 80px',
                                    gap: 2,
                                    p: 2,
                                    bgcolor: theme.palette.background.paper,
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                    borderTop: index > 0 ? 'none' : undefined,
                                    borderRadius: index === currentSuperPowers.length - 1 ? '0 0 8px 8px' : '0',
                                    alignItems: 'center',
                                    position: 'relative',
                                    cursor: 'move',
                                    opacity: draggedIndex === index ? 0.5 : 1,
                                    '&:hover': {
                                        bgcolor: alpha(getLevelColor(skill.level, index === 0), 0.1),
                                    },
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        bottom: 0,
                                        width: '4px',
                                        background: getLevelColor(skill.level, index === 0),
                                    },
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ color: getLevelColor(skill.level, index === 0) }}>
                                        {index === 0 ? <Sparkles size={16} /> : getLevelIcon(skill.level)}
                                    </Box>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 400,
                                            fontSize: { xs: '0.8rem', sm: '0.875rem', md: '0.9rem' },
                                        }}
                                    >
                                        {skill.title}
                                    </Typography>
                                    {index === 0 && (
                                        <Chip
                                            label="Focus"
                                            size="small"
                                            sx={{
                                                bgcolor: alpha(
                                                    theme.palette.accent?.main || theme.palette.secondary.main,
                                                    0.2,
                                                ),
                                                color: theme.palette.accent?.main || theme.palette.secondary.main,
                                                fontSize: '0.6rem',
                                                height: 18,
                                                fontWeight: 600,
                                                display: 'flex',
                                                alignItems: 'center',
                                                '& .MuiChip-label': {
                                                    lineHeight: 1,
                                                    paddingTop: 0,
                                                    paddingBottom: 0,
                                                },
                                            }}
                                        />
                                    )}
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <ItemBadge
                                        value={skill.level}
                                        title={`Level: ${getLevelText(skill.level)}`}
                                        icon={getAppIcon(EIconCategory.LEVEL)}
                                        level={skill.level}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <Chip
                                        label={getLevelText(skill.level)}
                                        size="small"
                                        sx={{
                                            bgcolor:
                                                theme.palette.mode === 'dark'
                                                    ? alpha(getLevelColor(skill.level, index === 0), 0.2)
                                                    : 'transparent',
                                            color: getLevelColor(skill.level, index === 0),
                                            border:
                                                theme.palette.mode === 'light'
                                                    ? `1px solid ${getLevelColor(skill.level, index === 0)}`
                                                    : 'none',
                                            fontWeight: 600,
                                            fontSize: '0.6rem',
                                            height: 24,
                                            borderRadius: '12px 1px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            minWidth: 'auto',
                                            '& .MuiChip-label': {
                                                lineHeight: 1,
                                                paddingTop: 0,
                                                paddingBottom: 0,
                                            },
                                        }}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                    <IconButton size="small" onClick={() => navigate(`/${ELinks.SKILLS}/${skill.id}`)}>
                                        <Eye size={16} />
                                    </IconButton>
                                    <IconButton size="small" onClick={() => handleDelete(skill.id)}>
                                        <X size={16} />
                                    </IconButton>
                                </Box>
                            </Box>
                        ))}
                        {availableSkills.length > 0 && (
                            <>
                                <Button
                                    variant="outlined"
                                    startIcon={<Plus size={16} />}
                                    onClick={(e) => setAddMenuAnchor(e.currentTarget)}
                                    sx={{ mt: 2, alignSelf: 'flex-start' }}
                                >
                                    SuperPower hinzufügen
                                </Button>

                                <Menu
                                    anchorEl={addMenuAnchor}
                                    open={Boolean(addMenuAnchor)}
                                    onClose={() => setAddMenuAnchor(null)}
                                    PaperProps={{
                                        sx: {
                                            maxHeight: 300,
                                            minWidth: 250,
                                        },
                                    }}
                                >
                                    {availableSkills.map((skill) => (
                                        <MenuItem key={skill.id} onClick={() => handleAddSkill(skill)}>
                                            <ListItemIcon>
                                                <Box sx={{ color: getLevelColor(skill.level) }}>
                                                    {getLevelIcon(skill.level)}
                                                </Box>
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={skill.title}
                                                secondary={`${getLevelText(skill.level)} (Level ${skill.level})`}
                                            />
                                        </MenuItem>
                                    ))}
                                </Menu>

                                <Snackbar
                                    open={showLimitError}
                                    autoHideDuration={4000}
                                    onClose={() => setShowLimitError(false)}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                                >
                                    <Alert
                                        onClose={() => setShowLimitError(false)}
                                        severity="info"
                                        sx={{ width: '100%' }}
                                    >
                                        Maximum 6 SuperPowers erreicht. Die unterste SuperPower wurde ersetzt.
                                    </Alert>
                                </Snackbar>
                            </>
                        )}

                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                mt: 3,
                                background: alpha(theme.palette.primary.main, 0.05),
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                borderRadius: 2,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Bot size={20} color={theme.palette.primary.main} />
                                <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 600, color: theme.palette.primary.main }}
                                >
                                    Bearbeitung
                                </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                Wähle bis zu 6 SuperPowers aus. Per Drag&Drop kannst du deine Focus SuperPower an erste
                                Stelle schieben, die dann in deiner Profil Card erscheint.
                            </Typography>
                        </Paper>
                    </Box>
                ) : (
                    // Card View
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                            gap: 2,
                        }}
                    >
                        {currentSuperPowers.map((skill, index) => (
                            <Paper
                                key={skill.id}
                                elevation={0}
                                sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    background: theme.palette.background.paper,
                                    border: `1px solid ${alpha(getLevelColor(skill.level, index === 0), 0.2)}`,
                                    position: 'relative',
                                    overflow: 'hidden',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    minHeight: 80,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: `0 4px 12px ${alpha(getLevelColor(skill.level, index === 0), 0.3)}`,
                                        borderColor: getLevelColor(skill.level, index === 0),
                                        bgcolor: alpha(getLevelColor(skill.level, index === 0), 0.05),
                                    },
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        bottom: 0,
                                        width: '4px',
                                        background: getLevelColor(skill.level, index === 0),
                                    },
                                }}
                                onClick={() => navigate(`/${ELinks.SKILLS}/${skill.id}`)}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        mb: 1,
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ color: getLevelColor(skill.level, index === 0) }}>
                                            {index === 0 ? <Sparkles size={20} /> : getLevelIcon(skill.level)}
                                        </Box>
                                        <Typography
                                            variant="subtitle1"
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
                                            }}
                                        >
                                            {skill.title}
                                        </Typography>
                                        {index === 0 && (
                                            <Chip
                                                label="Focus Power"
                                                size="small"
                                                sx={{
                                                    bgcolor: alpha(
                                                        theme.palette.accent?.main || theme.palette.secondary.main,
                                                        0.2,
                                                    ),
                                                    color: theme.palette.accent?.main || theme.palette.secondary.main,
                                                    fontSize: '0.6rem',
                                                    height: 20,
                                                    fontWeight: 600,
                                                }}
                                            />
                                        )}
                                    </Box>

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {!isMobile && (
                                            <Chip
                                                label={getLevelText(skill.level)}
                                                size="small"
                                                sx={{
                                                    bgcolor:
                                                        theme.palette.mode === 'dark'
                                                            ? alpha(getLevelColor(skill.level, index === 0), 0.2)
                                                            : 'transparent',
                                                    color: getLevelColor(skill.level, index === 0),
                                                    border:
                                                        theme.palette.mode === 'light'
                                                            ? `1px solid ${getLevelColor(skill.level, index === 0)}`
                                                            : 'none',
                                                    fontWeight: 600,
                                                    fontSize: '0.6rem',
                                                    height: 24,
                                                    borderRadius: '12px 1px',
                                                }}
                                            />
                                        )}
                                        <ItemBadge
                                            value={skill.level}
                                            title={`Level: ${getLevelText(skill.level)}`}
                                            icon={getAppIcon(EIconCategory.LEVEL)}
                                            level={skill.level}
                                            style={
                                                index === 0
                                                    ? {
                                                          backgroundColor: alpha(
                                                              theme.palette.accent?.main ||
                                                                  theme.palette.secondary.main,
                                                              0.2,
                                                          ),
                                                          color:
                                                              theme.palette.accent?.main ||
                                                              theme.palette.secondary.main,
                                                          border: `1px solid ${alpha(theme.palette.accent?.main || theme.palette.secondary.main, 0.3)}`,
                                                      }
                                                    : undefined
                                            }
                                        />
                                    </Box>
                                </Box>
                            </Paper>
                        ))}
                    </Box>
                )
            ) : (
                <Paper
                    elevation={0}
                    sx={{
                        p: { lg: 3, md: 2, xs: 2 },
                        textAlign: 'center',
                        background: alpha(theme.palette.primary.main, 0.05),
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        borderRadius: 2,
                    }}
                >
                    <Sparkles size={48} color={theme.palette.primary.main} style={{ opacity: 0.5, marginBottom: 16 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        Noch keine SuperPowers
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8, mb: 3 }}>
                        Wähle deine stärksten Skills aus und definiere deine SuperPowers
                    </Typography>

                    <Box sx={{ maxWidth: 400, mx: 'auto', mb: 3 }}>
                        <Autocomplete
                            value={searchValue}
                            onChange={(event, newValue) => {
                                if (newValue) {
                                    handleAddSkill(newValue);
                                    setSearchValue(null);
                                }
                            }}
                            options={availableSkills}
                            getOptionLabel={(option) => option.title}
                            renderOption={(props, option) => (
                                <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ color: getLevelColor(option.level) }}>{getLevelIcon(option.level)}</Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2">{option.title}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {getLevelText(option.level)} (Level {option.level})
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="SuperPower hinzufügen..."
                                    variant="outlined"
                                    size="small"
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <Search
                                                size={20}
                                                color={theme.palette.text.secondary}
                                                style={{ marginRight: 8 }}
                                            />
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            bgcolor: theme.palette.background.paper,
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: theme.palette.primary.main,
                                            },
                                        },
                                    }}
                                />
                            )}
                            noOptionsText="Keine Skills für SuperPowers verfügbar"
                            sx={{ mb: 2 }}
                        />

                        <Typography variant="caption" color="text.secondary">
                            Nur Skills mit höchstem oder zweithöchstem Level können als SuperPowers ausgewählt werden
                        </Typography>
                    </Box>

                    {eligibleSkills.length === 0 && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" sx={{ opacity: 0.8, mb: 2 }}>
                                Du hast noch keine Skills auf dem erforderlichen Level für SuperPowers
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<Target size={16} />}
                                onClick={() => navigate(`/${ELinks.PROFILE_EDIT_SKILLS}`)}
                            >
                                Skills entwickeln
                            </Button>
                        </Box>
                    )}
                </Paper>
            )}

            {currentSuperPowers.length > 0 && !editMode && (
                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        mt: 3,
                        background: alpha(theme.palette.primary.main, 0.05),
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        borderRadius: 2,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Bot size={20} color={theme.palette.primary.main} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                            SuperPower Status
                        </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Du hast {currentSuperPowers.length} SuperPower{currentSuperPowers.length !== 1 ? 's' : ''}{' '}
                        entwickelt!
                        {currentSuperPowers.filter((s) => s.level === maxLevel).length > 0 &&
                            ` Davon ${currentSuperPowers.filter((s) => s.level === maxLevel).length} auf höchstem Level.`}
                    </Typography>
                </Paper>
            )}

            {!editMode && currentSuperPowers.length > 0 && (
                <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<HandMetal size={16} />}
                        onClick={() => onTabChange?.(4)}
                        sx={{
                            textTransform: 'none',
                            flex: 1,
                        }}
                    >
                        Skills entwickeln
                    </Button>
                </Box>
            )}
        </Box>
    );
}

export default SuperPowersTab;
